import React, { useState } from 'react'
import { Card, Slider, Button, Space, Typography, Divider, Row, Col, Select } from 'antd'
import { PictureOutlined, RotateLeftOutlined, RotateRightOutlined, ZoomInOutlined, ZoomOutOutlined, ArrowsAltOutlined, ReloadOutlined, GlobalOutlined } from '@ant-design/icons'
import { useDesignStore } from '../../store/designStore'

const { Title, Text } = Typography

interface ImageTransformPanelProps {
  visible: boolean
  onClose: () => void
}

const ImageTransformPanel: React.FC<ImageTransformPanelProps> = ({ visible, onClose }) => {
  const { canvasSettings, updateBackgroundImageTransform, updateCanvasSettings } = useDesignStore()
  const [isDragging, setIsDragging] = useState(false)

  if (!visible || !canvasSettings.backgroundImage) return null

  const handleScaleChange = (value: number) => {
    updateBackgroundImageTransform({ scale: value / 100 })
  }

  const handleRotationXChange = (value: number) => {
    updateBackgroundImageTransform({ rotationX: (value * Math.PI) / 180 }) // Convert degrees to radians
  }

  const handleRotationYChange = (value: number) => {
    updateBackgroundImageTransform({ rotationY: (value * Math.PI) / 180 }) // Convert degrees to radians
  }

  const handleRotationZChange = (value: number) => {
    updateBackgroundImageTransform({ rotationZ: (value * Math.PI) / 180 }) // Convert degrees to radians
  }

  const handleOffsetXChange = (value: number) => {
    updateBackgroundImageTransform({ offsetX: value })
  }

  const handleOffsetYChange = (value: number) => {
    updateBackgroundImageTransform({ offsetY: value })
  }

  const handleOffsetZChange = (value: number) => {
    updateBackgroundImageTransform({ offsetZ: value })
  }

  const handleQuickRotateZ = (degrees: number) => {
    const currentRotation = (canvasSettings.backgroundImageRotationZ || 0) * 180 / Math.PI
    const newRotation = currentRotation + degrees
    updateBackgroundImageTransform({ rotationZ: (newRotation * Math.PI) / 180 })
  }

  const handleQuickScale = (factor: number) => {
    const currentScale = canvasSettings.backgroundImageScale || 1.0
    const newScale = Math.max(0.1, Math.min(5.0, currentScale * factor))
    updateBackgroundImageTransform({ scale: newScale })
  }

  const handleBackgroundModeChange = (mode: 'flat' | 'cylindrical' | 'spherical') => {
    updateCanvasSettings({ backgroundImageMode: mode })
  }

  const handleReset = () => {
    updateBackgroundImageTransform({
      scale: 1.0,
      rotationX: 0,
      rotationY: 0,
      rotationZ: 0,
      offsetX: 0,
      offsetY: 0,
      offsetZ: 0
    })
  }

  const currentRotationXDegrees = Math.round(((canvasSettings.backgroundImageRotationX || 0) * 180) / Math.PI)
  const currentRotationYDegrees = Math.round(((canvasSettings.backgroundImageRotationY || 0) * 180) / Math.PI)
  const currentRotationZDegrees = Math.round(((canvasSettings.backgroundImageRotationZ || 0) * 180) / Math.PI)
  const currentScale = Math.round((canvasSettings.backgroundImageScale || 1.0) * 100)

  return (
    <Card
      title={
        <Space>
          <PictureOutlined />
          <span>Image Transform</span>
        </Space>
      }
      extra={
        <Button type="text" onClick={onClose}>
          ×
        </Button>
      }
      style={{
        position: 'absolute',
        top: 80,
        left: 20,
        width: 380,
        maxHeight: 'calc(100vh - 100px)',
        overflowY: 'auto',
        zIndex: 1000,
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
      }}
      bodyStyle={{ maxHeight: 'calc(100vh - 180px)', overflowY: 'auto' }}
    >
      <Space direction="vertical" style={{ width: '100%' }}>
        {/* Background Mode Selector */}
        <div>
          <Text strong>
            <GlobalOutlined /> Background Mode
          </Text>
          <Select
            style={{ width: '100%', marginTop: 8 }}
            value={canvasSettings.backgroundImageMode || 'cylindrical'}
            onChange={handleBackgroundModeChange}
            options={[
              { value: 'flat', label: '📄 Flat Plane', disabled: false },
              { value: 'cylindrical', label: '🌅 360° Cylindrical (Recommended)' },
              { value: 'spherical', label: '🌍 Full Sphere' }
            ]}
          />
          <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginTop: 4 }}>
            {canvasSettings.backgroundImageMode === 'flat' && 'Image as flat plane on ground'}
            {canvasSettings.backgroundImageMode === 'cylindrical' && 'Image wraps around horizontally (best for landscapes)'}
            {canvasSettings.backgroundImageMode === 'spherical' && 'Image wraps around completely (360° sphere)'}
          </Text>
        </div>

        <Divider style={{ margin: '12px 0' }} />

        {/* Quick Actions */}
        <div>
          <Text strong>Quick Actions</Text>
          <Row gutter={8} style={{ marginTop: 8 }}>
            <Col span={6}>
              <Button 
                size="small" 
                icon={<RotateLeftOutlined />}
                onClick={() => handleQuickRotateZ(-90)}
                block
                title="Rotate Z -90°"
              />
            </Col>
            <Col span={6}>
              <Button 
                size="small" 
                icon={<RotateRightOutlined />}
                onClick={() => handleQuickRotateZ(90)}
                block
                title="Rotate Z +90°"
              />
            </Col>
            <Col span={6}>
              <Button 
                size="small" 
                icon={<ZoomOutOutlined />}
                onClick={() => handleQuickScale(0.8)}
                block
              />
            </Col>
            <Col span={6}>
              <Button 
                size="small" 
                icon={<ZoomInOutlined />}
                onClick={() => handleQuickScale(1.25)}
                block
              />
            </Col>
          </Row>
          
          {/* Visibility Presets */}
          <Row gutter={8} style={{ marginTop: 8 }}>
            <Col span={12}>
              <Button 
                size="small" 
                onClick={() => updateCanvasSettings({ backgroundImageOpacity: 0.3 })}
                block
                type="default"
              >
                Dim Background
              </Button>
            </Col>
            <Col span={12}>
              <Button 
                size="small" 
                onClick={() => updateCanvasSettings({ backgroundImageOpacity: 0.8 })}
                block
                type="default"
              >
                Bright Background
              </Button>
            </Col>
          </Row>
        </div>

        <Divider style={{ margin: '12px 0' }} />

        {/* Scale Control */}
        <div>
          <Text strong>Scale: {currentScale}%</Text>
          <Slider
            min={1}
            max={1000}
            step={1}
            value={currentScale}
            onChange={handleScaleChange}
            tooltip={{ formatter: (value) => `${value}%` }}
          />
          <Text type="secondary" style={{ fontSize: '12px' }}>
            Zoom the image in/out (1% to 1000%)
          </Text>
        </div>

        {/* Rotation Controls */}
        <div>
          <Text strong>Rotation</Text>
          
          <div style={{ marginTop: 8 }}>
            <Text>X-Axis (Pitch): {currentRotationXDegrees}°</Text>
            <Slider
              min={-360}
              max={360}
              step={1}
              value={currentRotationXDegrees}
              onChange={handleRotationXChange}
              tooltip={{ formatter: (value) => `${value}°` }}
            />
          </div>

          <div style={{ marginTop: 8 }}>
            <Text>Y-Axis (Yaw): {currentRotationYDegrees}°</Text>
            <Slider
              min={-360}
              max={360}
              step={1}
              value={currentRotationYDegrees}
              onChange={handleRotationYChange}
              tooltip={{ formatter: (value) => `${value}°` }}
            />
          </div>

          <div style={{ marginTop: 8 }}>
            <Text>Z-Axis (Roll): {currentRotationZDegrees}°</Text>
            <Slider
              min={-360}
              max={360}
              step={1}
              value={currentRotationZDegrees}
              onChange={handleRotationZChange}
              tooltip={{ formatter: (value) => `${value}°` }}
            />
          </div>

          <Text type="secondary" style={{ fontSize: '12px' }}>
            Rotate the image on all three axes for perfect alignment
          </Text>
        </div>

        <Divider style={{ margin: '12px 0' }} />

        {/* Position Controls */}
        <div>
          <Text strong>Position</Text>
          
          <div style={{ marginTop: 8 }}>
            <Text>X Offset: {Math.round((canvasSettings.backgroundImageOffsetX || 0) * 10) / 10}</Text>
            <Slider
              min={-100}
              max={100}
              step={0.1}
              value={canvasSettings.backgroundImageOffsetX || 0}
              onChange={handleOffsetXChange}
              tooltip={{ formatter: (value) => `${value} units` }}
            />
          </div>

          <div style={{ marginTop: 8 }}>
            <Text>Y Offset (Height): {Math.round((canvasSettings.backgroundImageOffsetY || 0) * 10) / 10}</Text>
            <Slider
              min={-50}
              max={50}
              step={0.1}
              value={canvasSettings.backgroundImageOffsetY || 0}
              onChange={handleOffsetYChange}
              tooltip={{ formatter: (value) => `${value} units` }}
            />
          </div>

          <div style={{ marginTop: 8 }}>
            <Text>Z Offset: {Math.round((canvasSettings.backgroundImageOffsetZ || 0) * 10) / 10}</Text>
            <Slider
              min={-100}
              max={100}
              step={0.1}
              value={canvasSettings.backgroundImageOffsetZ || 0}
              onChange={handleOffsetZChange}
              tooltip={{ formatter: (value) => `${value} units` }}
            />
          </div>

          <Text type="secondary" style={{ fontSize: '12px' }}>
            Pan the image around the canvas
          </Text>
        </div>

        <Divider style={{ margin: '12px 0' }} />

        {/* Background Opacity Control */}
        <div>
          <Text strong>Background Opacity: {Math.round((canvasSettings.backgroundImageOpacity || 0.7) * 100)}%</Text>
          <Slider
            min={0}
            max={100}
            step={1}
            value={Math.round((canvasSettings.backgroundImageOpacity || 0.7) * 100)}
            onChange={(value) => updateCanvasSettings({ backgroundImageOpacity: value / 100 })}
            tooltip={{ formatter: (value) => `${value}%` }}
          />
          <Text type="secondary" style={{ fontSize: '12px' }}>
            Lower opacity to see ADUs/Pools more clearly against background
          </Text>
        </div>

        <Divider style={{ margin: '12px 0' }} />

        {/* Reset Button */}
        <Button 
          icon={<ReloadOutlined />} 
          onClick={handleReset}
          block
          type="dashed"
        >
          Reset Transform
        </Button>

        {/* Tips */}
        <div style={{ 
          background: '#f6ffed', 
          border: '1px solid #b7eb8f', 
          borderRadius: 6, 
          padding: 12,
          marginTop: 8
        }}>
          <Text style={{ fontSize: '12px', color: '#52c41a' }}>
            <strong>💡 Tips:</strong><br />
            • Use quick actions for common transforms<br />
            • Position the image to align with your property<br />
            • 3D terrain will follow image transformations
          </Text>
        </div>
      </Space>
    </Card>
  )
}

export default ImageTransformPanel
